import {
  Document,
  Font,
  Page,
  pdf,
  StyleSheet,
  Text,
  View
} from "@react-pdf/renderer"
import moment from "moment"
import { convertNumber, useHandleAuth } from "utils"
import { MapJournalList } from "../../types"

type UseExportPdfJournal = {
  data: MapJournalList[]
  module?: string
}

export const useExportPdfJournal = ({ data, module }: UseExportPdfJournal) => {
  const auth = useHandleAuth()
  const token = auth.userTokenData()

  return {
    handleExportPdf: ({ tab }: { tab: string }) =>
      pdf(<TableJournal tab={tab} companyName={token?.company_name ?? ""} data={data} module={module} />)
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = `journal-${tab}.pdf`
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

type JournalProps = {
  module?: string
  companyName: string
  data: MapJournalList[]
  tab: string
}

export const TableJournal = ({ companyName, data, tab, module }: JournalProps) => {
  const columns = ["DATE", "ID", "ACCOUNT", "MEMO", "DEBIT", "CREDIT", "JOB ID"]

  Font.register({
    family: "Roboto",
    fonts: [
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxP.ttf" // Regular
      },
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf", // Bold
        fontWeight: "bold"
      }
    ]
  })

  const styles = StyleSheet.create({
    page: {
      paddingHorizontal: 15,
      paddingBottom: 35,
      paddingTop: 15,
    },
    table: {
      display: "flex",
      width: "auto",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#fff"
    },
    tableRow: {
      flexDirection: "row" 
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#000000",
      color: "#FFFFFF"
    },
    tableCol: {
      flex: 1,
      borderBottomStyle: "solid",
      borderBottomWidth: 1,
      borderBottomColor: "#0aeaeb4",
    },
    tableCell: {
      margin: 2,
      fontSize: 8,
      fontFamily: "Roboto"
    },
    headerText: {
      margin: 2,
      fontSize: 9,
      fontWeight: "bold",
      fontFamily: "Roboto",
      textAlign: "right"
    }
  })

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {module && <Text
          style={{
            fontSize: 18,
            marginTop: 30,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "heavy",
            textTransform: "uppercase"
          }}>
          {module}
        </Text>}

        <Text
          style={{
            fontSize: 18,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "heavy",
            textTransform: "uppercase"
          }}>
          Journal - {tab}
        </Text>

        <Text
          style={{
            fontSize: 12,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "medium"
          }}>
          {companyName?.toUpperCase() ?? ""}
        </Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            {columns.map((col, index: number) => (
              <View key={index} style={[styles.tableCol]}>
                <Text style={[styles.headerText, { textAlign: "center" }]}>
                  {col}
                </Text>
              </View>
            ))}
          </View>

          {data.map((item, _pIndex: number) => {
            return item.data.data.map((data, cIndex) => {
              const isHeader = cIndex === 0
              const isFooter = cIndex === item.data.data.length - 1

              if (isHeader) {
                return (
                  <View
                    wrap={false}
                    key={cIndex}
                    style={[styles.tableRow, { fontWeight: "bold" }]}>
                    <View style={[styles.tableCol]}>
                      <Text style={[styles.tableCell]}>
                        {moment(item.date).format("DD/MM/YYYY")}
                      </Text>
                    </View>

                    <View style={[styles.tableCol]}>
                      <Text style={[styles.tableCell]}>
                        {" "}
                        {item.data.source_memo}
                      </Text>
                    </View>

                    {Array(5)
                      .fill(null)
                      .map((_, index) => (
                        <View key={index} style={[styles.tableCol]}>
                          <Text style={[styles.tableCell]} />
                        </View>
                      ))}
                  </View>
                )
              }

              if (isFooter) {
                const checkerDebit = data?.checkerDebit ?? 0
                const checkerCredit = data?.checkerCredit ?? 0

                return (
                  <View
                    wrap={false}
                    key={cIndex}
                    style={[styles.tableRow, { fontWeight: "bold", borderBottomColor: "#fff", paddingBottom: 5 }]}>
                    {Array(3)
                      .fill(null)
                      .map((_, index) => (
                        <View key={index} style={[styles.tableCol, { borderBottomColor: "#fff" }]} />
                      ))}

                    <View style={[styles.tableCol, { borderBottomColor: "#fff" }]}>
                      <Text style={[styles.tableCell]}>{data.memo}</Text>
                    </View>

                    <View style={[styles.tableCol, { borderBottomColor: "#fff" }]}>
                      <Text style={[styles.tableCell, { textAlign: "right" }]}>
                        {checkerDebit > 0 ? convertNumber(checkerDebit).intoCurrency : `${convertNumber(Math.abs(checkerDebit)).intoCurrency}`}
                      </Text>
                    </View>

                    <View style={[styles.tableCol, { borderBottomColor: "#fff" }]}>
                      <Text
                        style={[
                          styles.tableCell,
                          { color: "#F87171", textAlign: "right" }
                        ]}>
                        {checkerCredit > 0 ? convertNumber(checkerCredit).intoCurrency : `${convertNumber(Math.abs(checkerCredit)).intoCurrency}`}
                      </Text>
                    </View>

                    <View key={cIndex} style={[styles.tableCol, { borderBottomColor: "#fff" }]} />
                  </View>
                )
              }

              return (
                <View
                  wrap={false}
                  key={cIndex} style={styles.tableRow}>
                  <View key={cIndex} style={[styles.tableCol]} />
                  <View style={[styles.tableCol]}>
                    <Text
                      style={[
                        styles.tableCell
                      ]}>{`${data.source_code} ${data.trx_code}`}</Text>
                  </View>

                  <View style={[styles.tableCol]}>
                    <Text
                      style={[
                        styles.tableCell
                      ]}>{`${data.coa_code} ${data.coa_name}`}</Text>
                  </View>

                  <View style={[styles.tableCol]}>
                    <Text style={[styles.tableCell]}>{data.memo}</Text>
                  </View>

                  <View style={[styles.tableCol]}>
                    <Text style={[styles.tableCell, { textAlign: "right" }]}>
                      {data.position === 1
                        ? data.amount > 0
                          ? convertNumber(data.amount).intoCurrency
                          : convertNumber(Math.abs(data.amount)).intoCurrency
                        : ""}
                    </Text>
                  </View>

                  <View style={[styles.tableCol]}>
                    <Text
                      style={[
                        styles.tableCell,
                        { color: "#F87171", textAlign: "right" }
                      ]}>
                      {data.position === 2
                        ? data.amount > 0
                          ? convertNumber(data.amount).intoCurrency
                          : convertNumber(Math.abs(data.amount)).intoCurrency
                        : ""}
                    </Text>
                  </View>

                  <View style={[styles.tableCol]}>
                    <Text style={[styles.tableCell]}>{data.job_id}</Text>
                  </View>
                </View>
              )
            })
          })}
        </View>
      </Page>
    </Document>
  )
}
